package com.speedsoftware.nibbler.ui.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.TwoFactorSetupResponse
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.components.QrCodeImage
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

/** Profile card for turning authenticator-app two-factor authentication on or off. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun TwoFactorSection(appState: AppState) {
    val user = appState.currentUser ?: return
    val scope = rememberCoroutineScope()
    val clipboard = LocalClipboardManager.current

    var setup by remember { mutableStateOf<TwoFactorSetupResponse?>(null) }
    var recoveryCodes by remember { mutableStateOf<List<String>?>(null) }
    var code by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var message by remember { mutableStateOf<String?>(null) }
    var isBusy by remember { mutableStateOf(false) }
    var showDisable by remember { mutableStateOf(false) }
    var showRegenerate by remember { mutableStateOf(false) }

    fun resetFields() { code = ""; password = ""; message = null }

    Column(
        modifier = Modifier
            .widthIn(max = 560.dp)
            .fillMaxWidth()
            .background(NibblerColors.SurfaceContainer, RoundedCornerShape(8.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp))
            .padding(20.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        Text("Two-factor authentication", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = NibblerColors.OnSurface)

        val codes = recoveryCodes
        val pending = setup
        when {
            // 1. Freshly generated recovery codes, shown once
            codes != null -> {
                Text(
                    "Save these recovery codes somewhere safe. Each can be used once if you lose access to your authenticator app. They will not be shown again.",
                    fontSize = 13.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
                FlowRow(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    codes.forEach { recovery ->
                        Text(
                            recovery,
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 14.sp,
                            color = NibblerColors.OnSurface,
                            modifier = Modifier
                                .background(NibblerColors.SurfaceContainerHigh, RoundedCornerShape(8.dp))
                                .padding(horizontal = 10.dp, vertical = 6.dp)
                        )
                    }
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedButton(
                        onClick = { clipboard.setText(AnnotatedString(codes.joinToString("\n"))); message = "Copied to the clipboard." },
                        shape = RoundedCornerShape(8.dp),
                        border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
                    ) { Text("Copy codes", color = NibblerColors.OnSurface) }
                    Button(
                        onClick = { recoveryCodes = null; resetFields() },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                    ) { Text("I have saved them") }
                }
                message?.let { Text(it, fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant) }
            }

            // 2. Enrolment: scan the QR code, then prove it works
            pending != null -> {
                Text(
                    "1. Open an authenticator app (Google Authenticator, Microsoft Authenticator, Authy, 1Password, ...) and scan this QR code.",
                    fontSize = 13.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
                QrCodeImage(text = pending.otpauthUri, size = 200.dp, modifier = Modifier.align(Alignment.CenterHorizontally))
                Text(
                    "Cannot scan? Enter this key manually:",
                    fontSize = 12.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
                Text(
                    pending.secret.chunked(4).joinToString(" "),
                    fontFamily = FontFamily.SansSerif,
                    fontSize = 14.sp,
                    color = NibblerColors.OnSurface
                )
                Text(
                    "2. Enter the 6-digit code the app shows to confirm.",
                    fontSize = 13.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
                OutlinedTextField(
                    value = code,
                    onValueChange = { code = it.filter { c -> c.isDigit() }.take(6); message = null },
                    label = { Text("6-digit code") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                message?.let { Text(it, fontSize = 12.sp, color = NibblerColors.Error) }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Button(
                        enabled = !isBusy && code.length == 6,
                        onClick = {
                            isBusy = true
                            scope.launch {
                                appState.apiClient.enableTwoFactor(code).onSuccess {
                                    appState.currentUser = user.copy(twoFactorEnabled = true)
                                    recoveryCodes = it.recoveryCodes
                                    setup = null
                                    resetFields()
                                }.onFailure { message = it.message ?: "That code is not valid." }
                                isBusy = false
                            }
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                    ) { Text("Verify and enable") }
                    TextButton(onClick = { setup = null; resetFields() }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
                }
            }

            // 3. Enabled
            user.twoFactorEnabled -> {
                Text(
                    "Two-factor authentication is ON. Logging in needs your password and a code from your authenticator app.",
                    fontSize = 13.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedButton(
                        onClick = { resetFields(); showRegenerate = true },
                        shape = RoundedCornerShape(8.dp),
                        border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
                    ) { Text("New recovery codes", color = NibblerColors.OnSurface) }
                    OutlinedButton(
                        onClick = { resetFields(); showDisable = true },
                        shape = RoundedCornerShape(8.dp),
                        border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
                    ) { Text("Turn off", color = NibblerColors.Error) }
                }
            }

            // 4. Not enabled
            else -> {
                Text(
                    "Protect your account with a second step at login: a 6-digit code from an authenticator app on your phone.",
                    fontSize = 13.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
                message?.let { Text(it, fontSize = 12.sp, color = NibblerColors.Error) }
                Button(
                    enabled = !isBusy,
                    onClick = {
                        isBusy = true
                        scope.launch {
                            appState.apiClient.setupTwoFactor()
                                .onSuccess { setup = it; resetFields() }
                                .onFailure { message = it.message ?: "Could not start the setup." }
                            isBusy = false
                        }
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                ) { Text("Enable two-factor authentication") }
            }
        }
    }

    if (showDisable) {
        AlertDialog(
            onDismissRequest = { showDisable = false },
            containerColor = NibblerColors.SurfaceContainerHigh,
            shape = RoundedCornerShape(8.dp),
            title = { Text("Turn off two-factor authentication", color = NibblerColors.OnSurface) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Confirm with your password and a current code (or a recovery code).", fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant)
                    OutlinedTextField(
                        value = password, onValueChange = { password = it; message = null },
                        label = { Text("Password") }, singleLine = true,
                        visualTransformation = PasswordVisualTransformation(), modifier = Modifier.fillMaxWidth()
                    )
                    OutlinedTextField(
                        value = code, onValueChange = { code = it; message = null },
                        label = { Text("Code") }, singleLine = true, modifier = Modifier.fillMaxWidth()
                    )
                    message?.let { Text(it, fontSize = 12.sp, color = NibblerColors.Error) }
                }
            },
            confirmButton = {
                Button(
                    enabled = !isBusy && password.isNotBlank() && code.isNotBlank(),
                    onClick = {
                        isBusy = true
                        scope.launch {
                            appState.apiClient.disableTwoFactor(password, code).onSuccess {
                                appState.currentUser = user.copy(twoFactorEnabled = false)
                                showDisable = false
                                resetFields()
                            }.onFailure { message = it.message ?: "Could not turn off two-factor authentication." }
                            isBusy = false
                        }
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.ErrorContainer, contentColor = NibblerColors.Error)
                ) { Text("Turn off") }
            },
            dismissButton = { TextButton(onClick = { showDisable = false }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) } }
        )
    }

    if (showRegenerate) {
        AlertDialog(
            onDismissRequest = { showRegenerate = false },
            containerColor = NibblerColors.SurfaceContainerHigh,
            shape = RoundedCornerShape(8.dp),
            title = { Text("New recovery codes", color = NibblerColors.OnSurface) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("This replaces your existing recovery codes. Confirm with a current code.", fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant)
                    OutlinedTextField(
                        value = code, onValueChange = { code = it; message = null },
                        label = { Text("Code") }, singleLine = true, modifier = Modifier.fillMaxWidth()
                    )
                    message?.let { Text(it, fontSize = 12.sp, color = NibblerColors.Error) }
                }
            },
            confirmButton = {
                Button(
                    enabled = !isBusy && code.isNotBlank(),
                    onClick = {
                        isBusy = true
                        scope.launch {
                            appState.apiClient.regenerateRecoveryCodes(code).onSuccess {
                                recoveryCodes = it.recoveryCodes
                                showRegenerate = false
                                resetFields()
                            }.onFailure { message = it.message ?: "That code is not valid." }
                            isBusy = false
                        }
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                ) { Text("Generate") }
            },
            dismissButton = { TextButton(onClick = { showRegenerate = false }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) } }
        )
    }
}
